import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type { AIRateLimitStatus, DocumentUpload } from '@apiTypes'

// Work in flight captures this before it starts and drops its result if it no longer matches, so
// neither a read nor a refusal belonging to a torn-down session can write into the next one.
let generation = 0

// Shared so a banner and a dialog mounting in the same tick issue one GET rather than racing two.
// Same shape as `_refreshTokenRequest` in api/api.ts.
let inFlightRead: Promise<void> | null = null
let inFlightId = 0

interface AIState {
  rateLimitStatus: AIRateLimitStatus | null

  // Survives a status read that never answers, so a refusal seen first-hand keeps surfaces blocked.
  refusedByRateLimit: boolean

  // Surfaces render nothing until the first read settles — defaulting to "allowed" would show an
  // over-limit workspace the promo banner on every load.
  rateLimitSettled: boolean
}

export const useAIStore = defineStore('ai', {
  state: (): AIState => ({
    rateLimitStatus: null,
    refusedByRateLimit: false,
    rateLimitSettled: false,
  }),

  getters: {
    rateLimitReached: (state): boolean => state.refusedByRateLimit || (state.rateLimitStatus?.overLimit ?? false),
  },

  actions: {
    extractRequestData(data: DocumentUpload, file?: File | null) {
      const payload = file ? { ...data, file } as unknown as DocumentUpload : data

      return api.ai.aiExtractRequestDataFromDocumentCreate(payload)
    },

    /** Capture before a request whose outcome writes here later, and pass back to `markRateLimitReached`. */
    rateLimitGeneration(): number {
      return generation
    },

    fetchRateLimitStatus(): Promise<void> {
      if (inFlightRead)
        return inFlightRead

      const started = generation
      const id = ++inFlightId

      inFlightRead = (async (): Promise<void> => {
        try {
          const { data } = await api.ai.aiRateLimitStatusRetrieve()

          if (started !== generation)
            return

          this.rateLimitStatus = data
          this.refusedByRateLimit = false
        }
        catch (error) {
          console.error('Error fetching AI rate limit status:', error)
        }
        finally {
          // A failed read still settles, or a dead endpoint would hide the AI entry point for good.
          // A superseded one must not: either the session is gone, or a refusal already settled it.
          if (started === generation)
            this.rateLimitSettled = true

          // Only ever clear our own handle — a reset may have started a newer read already.
          if (id === inFlightId)
            inFlightRead = null
        }
      })()

      return inFlightRead
    },

    resetRateLimit(): void {
      generation++
      inFlightRead = null
      this.$reset()
    },

    markRateLimitReached(started: number): void {
      if (started !== generation)
        return

      generation++
      this.refusedByRateLimit = true
      this.rateLimitSettled = true
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useAIStore, import.meta.hot))
